import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-5 py-3.5 text-sm font-light text-white placeholder:text-white/30 backdrop-blur-md transition-[border-color,background-color] duration-300 focus:border-white/40 focus:bg-white/[0.07]";

const Field = ({ label, name, type = "text", required = false, textarea = false, options = null, value, onChange, testId, placeholder }) => (
  <label className="block">
    <span className="mb-2 block font-display text-[11px] font-medium tracking-[0.25em] text-white/50 uppercase">
      {label} {required && <span className="text-white/80">*</span>}
    </span>
    {textarea ? (
      <textarea data-testid={testId} name={name} rows={4} value={value} onChange={onChange} placeholder={placeholder} className={`${inputCls} resize-none`} />
    ) : options ? (
      <select data-testid={testId} name={name} value={value} onChange={onChange} className={inputCls}>
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    ) : (
      <input data-testid={testId} type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} className={inputCls} />
    )}
  </label>
);

const INITIAL = {
  name: "", business_name: "", email: "", phone: "", business_description: "",
  website_type: "", package_interest: "", has_media: "", project_details: "",
};

const Contact = ({ selectedPackage, onLaunched }) => {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (selectedPackage) {
      setForm((f) => ({ ...f, package_interest: selectedPackage }));
    }
  }, [selectedPackage]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Please add at least your name and email so we can reach you.");
      return;
    }
    setStatus("sending");
    try {
      const body = new URLSearchParams({
        "form-name": "closer-enquiry",
        ...form,
      }).toString();
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      if (!res.ok) throw new Error("Request failed");
      setForm(INITIAL);
      setStatus("idle");
      onLaunched();
    } catch {
      setStatus("idle");
      setError("Something went wrong sending your enquiry. Please try again.");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative z-10 py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        <SectionHeading
          kicker="Contact"
          title="Launch Your Project."
          sub="Tell us where your business is going — we'll build the website that takes it there. We reply to every enquiry personally."
          testId="contact-heading"
        />

        <Reveal i={2}>
          <div className="relative mx-auto mt-16 max-w-3xl overflow-hidden rounded-[2rem] glass p-7 sm:p-12">
            <div className="pointer-events-none absolute -top-32 left-1/2 h-64 w-[36rem] -translate-x-1/2 rounded-full bg-white/[0.06] blur-3xl" />
            <AnimatePresence mode="wait">
              <motion.form
                key="form"
                name="closer-enquiry"
                method="POST"
                data-netlify="true"
                data-testid="enquiry-form"
                  onSubmit={onSubmit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, filter: "blur(10px)" }}
                  className="relative grid grid-cols-1 gap-6 sm:grid-cols-2"
                >
                  <Field label="Name" name="name" required value={form.name} onChange={onChange} testId="enquiry-name-input" placeholder="Your name" />
                  <Field label="Business Name" name="business_name" value={form.business_name} onChange={onChange} testId="enquiry-business-input" placeholder="Your business" />
                  <Field label="Email" name="email" type="email" required value={form.email} onChange={onChange} testId="enquiry-email-input" placeholder="you@business.com" />
                  <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} testId="enquiry-phone-input" placeholder="Optional" />
                  <div className="sm:col-span-2">
                    <Field label="What does your business do?" name="business_description" value={form.business_description} onChange={onChange} testId="enquiry-business-description-input" placeholder="A quick overview of what you do" />
                  </div>
                  <Field label="What type of website do you need?" name="website_type" options={["Business Website", "Lead-Generation Website", "Portfolio or Showcase Website", "Booking-Request Website", "Website Redesign", "Something Else"]} value={form.website_type} onChange={onChange} testId="enquiry-website-type-select" />
                  <Field label="Which package are you interested in?" name="package_interest" options={["CLOSER. LAUNCH — £399", "CLOSER. GROWTH — £699", "CLOSER. BLACK LABEL — From £999", "Not Sure Yet"]} value={form.package_interest} onChange={onChange} testId="enquiry-package-select" />
                  <div className="sm:col-span-2">
                    <Field label="Do you have your own images?" name="has_media" options={["Yes, I have my own images/videos", "No, I don't have images/videos", "A mix of both", "I need help"]} value={form.has_media} onChange={onChange} testId="enquiry-media-select" />
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Project Details" name="project_details" textarea value={form.project_details} onChange={onChange} testId="enquiry-details-textarea" placeholder="Tell us about your project, goals and timeline" />
                  </div>
                  {error && (
                    <p data-testid="enquiry-error" className="sm:col-span-2 rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm text-white/80">
                      {error}
                    </p>
                  )}
                  <div className="sm:col-span-2">
                    <button
                      data-testid="enquiry-submit-button"
                      type="submit"
                      disabled={status === "sending"}
                      className="group inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-8 py-4 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
                    >
                      {status === "sending" ? (
                        <>Launching <Loader2 size={16} className="animate-spin" /></>
                      ) : (
                        <>Launch My Project <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" /></>
                      )}
                    </button>
                  </div>
                </motion.form>
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
